import { useEffect, useState, useCallback, useRef } from 'react';
import { API_URL } from '../config/api';

/* global EventSource, URL */

export function useSSENotifications(onNotification) {
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetryInterval = 60000; // 最大60秒

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // ページが非表示の時は接続しない
    if (document.hidden) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const url = new URL(`${API_URL}/api/notifications/stream`);
      url.searchParams.set('token', token);

      const eventSource = new EventSource(url.toString(), {
        withCredentials: true,
      });

      eventSource.onopen = () => {
        setConnected(true);
        retryCountRef.current = 0; // 成功したらリセット
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      eventSource.onerror = () => {
        setConnected(false);
        eventSource.close();

        if (!reconnectTimeoutRef.current) {
          // 指数バックオフ: 5s → 10s → 20s → 40s → 60s(上限)
          const delay = Math.min(5000 * Math.pow(2, retryCountRef.current), maxRetryInterval);
          retryCountRef.current += 1;
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect();
          }, delay);
        }
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type !== 'connected' && onNotification) {
            onNotification(data);
          }
        } catch {
          // パースエラーは無視
        }
      };

      eventSourceRef.current = eventSource;
    } catch {
      setConnected(false);
    }
  }, [onNotification]);

  useEffect(() => {
    connect();

    // ページ表示/非表示でSSEを切り替え
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // バックグラウンド → 切断
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        setConnected(false);
      } else {
        // フォアグラウンド復帰 → 再接続
        retryCountRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);

  return connected;
}